import styled from '@emotion/styled';
import {
  palette,
  status,
  gradient,
  spacing,
  radius,
  shadow,
  typography,
  transition,
  duration,
  easing,
  layout,
} from '../../../../style-utils/theme';
import { up, down } from '../../../../style-utils/media-queries';
import { fadeIn, fadeInUp } from '../../../../style-utils/motion';
import { m } from '../../../../motion';

/* ─────────────────────────  Shell  ───────────────────────── */

export const DashboardContent = styled.div`
  min-height: 100%;
  width: 100%;
  background: transparent;
  border-radius: ${radius.md};
  animation: ${fadeIn} ${duration.base} ${easing.standard} both;
`;

export const ScopesListing = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  background: ${palette.surface};
  border-bottom: 1px solid ${palette.line};
  border-radius: ${radius.md} ${radius.md} 0 0;

  ${up('md')} {
    padding: ${spacing.md};
    gap: ${spacing.md};
    flex-wrap: nowrap;
  }
`;

export const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin-bottom: ${spacing.xs};
`;

export const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 200px;
  max-width: 320px;
`;

export const StyledInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 16px;
  letter-spacing: ${typography.tracking.snug};
  transition: ${transition.colors};

  ::placeholder {
    color: ${palette.slateLight};
  }

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

/* ─────────────────────────  Layout primitives  ───────────────────────── */

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${spacing.sm};

  ${up('md')} {
    padding: ${spacing.md};
  }
`;

export const Row = styled.div`
  display: flex;
  flex: 1;
`;

/* Card visual for a table row (and its header variant). */
export const StyledFlex = styled.div<{ isHeaderRow?: boolean }>`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
  overflow: visible;
  transition: ${transition.base};

  ${down('md')} {
    flex-direction: column;
    padding: ${spacing.sm};
  }

  ${({ isHeaderRow }) =>
    isHeaderRow
      ? `
        background: transparent;
        border: none;
        box-shadow: none;
      `
      : `
        &:hover {
          background: ${palette.irisTintSoft};
          border-color: ${palette.lineStrong};
          box-shadow: ${shadow.sm};
          transform: translateY(-1px);
        }
      `}
`;

export const StyledFlexRow = styled(Row)<{ isHeaderRow?: boolean }>`
  flex: 0 0 auto;
  width: 100%;
  border-radius: ${radius.md};
  margin-bottom: ${spacing.xs};
  animation: ${fadeInUp} ${duration.base} ${easing.entrance} backwards;

  &:first-of-type {
    margin-top: ${spacing.xs};
  }

  ${({ isHeaderRow }) =>
    isHeaderRow &&
    `
      animation: none;
      ${down('md')} {
        display: none;
      }
    `}
`;

/* total desktop cell widths must sum to 100 */
export const ItemContainer = styled.div<{
  width?: number;
  isColumnHeader?: boolean;
  alignEnd?: boolean;
}>`
  box-sizing: border-box;
  width: ${({ width }) => (width ? `${width}%` : '100%')};
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  overflow: visible;
  padding: ${spacing.sm};
  background: transparent;

  ${up('md')} {
    height: 64px;
    justify-content: ${({ alignEnd }) =>
      alignEnd ? 'flex-end' : 'flex-start'};
  }

  ${({ isColumnHeader }) =>
    isColumnHeader &&
    `
      text-transform: uppercase;
      height: auto;
    `}

  ${down('md')} {
    width: 100%;
    padding: ${spacing.xs} 0;
    border-bottom: 1px solid ${palette.line};

    &:last-child {
      border-bottom: none;
    }

    &[data-label] {
      justify-content: space-between;

      &::before {
        content: attr(data-label);
        color: ${palette.slate};
        font-family: ${typography.ui};
        font-size: 12px;
        font-weight: ${typography.weight.semibold};
        letter-spacing: ${typography.tracking.caps};
        text-transform: uppercase;
        flex: 0 0 auto;
      }
    }
  }
`;

export const TruncatedCell = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${typography.ui};
  font-size: 14px;
  color: ${palette.slate};
  letter-spacing: ${typography.tracking.snug};

  /* Below md each row is a stacked card with the value beside its label, so
     at 320 a name or an email had ~170px and lost its end to the ellipsis
     (device sweep, QA-13). There the whole value wraps instead; the table
     layout above md keeps the one-line ellipsis and its title. */
  ${down('md')} {
    white-space: normal;
    overflow: visible;
    overflow-wrap: anywhere;
    text-overflow: clip;
    text-align: right;
  }
`;

/* ─────────────────────────  Avatar  ───────────────────────── */

export const Avatar = styled.div`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: ${radius.circle};
  padding: ${spacing.half};
  background: linear-gradient(
    135deg,
    ${palette.iris} 0%,
    ${palette.irisDark} 100%
  );
  box-shadow: ${shadow.xs};
  display: grid;
  place-items: center;
`;

export const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  border-radius: ${radius.circle};
  background: ${palette.irisTint};
  color: ${palette.irisDark};
  display: grid;
  place-items: center;
  overflow: hidden;
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
`;

export const AvatarLarge = styled(Avatar)`
  width: 56px;
  height: 56px;
`;

export const AvatarFallbackLarge = styled(AvatarFallback)`
  font-size: 18px;
`;

/* ─────────────────────────  Pills  ───────────────────────── */

export const RolePill = styled.div<{ isOwner?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  min-height: 24px;
  padding: ${spacing.half} ${spacing.sm};
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  background: ${({ isOwner }) =>
    isOwner ? status.brand.bg : status.neutral.bg};
  color: ${({ isOwner }) => (isOwner ? status.brand.fg : status.neutral.fg)};

  &::before {
    content: '';
    width: ${spacing.xs};
    height: ${spacing.xs};
    border-radius: ${radius.circle};
    flex: 0 0 auto;
    background: ${({ isOwner }) =>
      isOwner ? status.brand.dot : status.neutral.dot};
  }
`;

export type PillTone = 'brand' | 'neutral' | 'success' | 'warning' | 'alert';

const toneOf = (tone: PillTone) => status[tone];

/* Count / label pill without a leading dot — used for grant and building
   counts, scope types and capability tags. */
export const CountPill = styled.span<{ tone?: PillTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.half};
  min-height: 24px;
  padding: ${spacing.half} ${spacing.sm};
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  background: ${({ tone = 'neutral' }) => toneOf(tone).bg};
  color: ${({ tone = 'neutral' }) => toneOf(tone).fg};
`;

/* Scope type badge. An organization grant reads brand (widest reach), a unit
   grant reads neutral (narrowest) — the tint itself carries the breadth. */
export const ScopeTypePill = styled(CountPill)`
  text-transform: uppercase;
  letter-spacing: ${typography.tracking.caps};
  font-size: 12px;
`;

export const CapabilityTag = styled.span<{ tone?: PillTone; muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: 24px;
  padding: ${spacing.half} ${spacing.sm};
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  background: ${({ tone = 'neutral', muted }) =>
    muted ? palette.surfaceMuted : toneOf(tone).bg};
  color: ${({ tone = 'neutral', muted }) =>
    muted ? palette.slateLight : toneOf(tone).fg};
  border: 1px solid ${({ muted }) => (muted ? palette.line : 'transparent')};
`;

/* "via …" chip explaining which grant produced a resolved building. */
export const ViaTag = styled.span<{ tone?: PillTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: 24px;
  padding: ${spacing.half} ${spacing.sm};
  border-radius: ${radius.pill};
  background: ${({ tone = 'neutral' }) => toneOf(tone).bg};
  color: ${({ tone = 'neutral' }) => toneOf(tone).fg};
  font-family: ${typography.ui};
  font-size: 12px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
`;

/* ─────────────────────────  Buttons  ───────────────────────── */

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  border: none;
  cursor: pointer;
  border-radius: ${radius.sm};
  background: ${gradient.primary};
  color: ${palette.white};
  box-shadow: ${shadow.brand};
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  &:hover:not(:disabled) {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.brand}, ${shadow.focus};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: none;
  }

  ${up('lg')} {
    min-height: ${layout.controlHeight};
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  color: ${palette.slate};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  &:hover:not(:disabled) {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lineStrong};
    color: ${palette.inkSoft};
  }
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  ${up('lg')} {
    min-height: ${layout.controlHeight};
  }
`;

export const ManageButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-height: ${layout.buttonHeight};
  padding: ${spacing.half} ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.irisTintSoft};
  border: 1px solid ${palette.line};
  color: ${palette.irisDark};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTint};
    border-color: ${palette.lineStrong};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${down('md')} {
    min-height: ${layout.touchTarget};
  }
`;

export const RevokeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.buttonHeight};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  color: ${palette.slate};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  &:hover:not(:disabled) {
    background: ${palette.alertSoft};
    border-color: ${palette.alert};
    color: ${status.alert.fg};
  }
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focusError};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  ${down('md')} {
    min-height: ${layout.touchTarget};
  }
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  min-height: ${layout.controlHeight};
  padding: 0 ${spacing.sm} 0 ${spacing.xs};
  background: transparent;
  border: none;
  border-radius: ${radius.sm};
  color: ${palette.slate};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
    color: ${palette.irisDark};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* ─────────────────────────  Detail: staff header  ───────────────────────── */

export const DetailHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  animation: ${fadeInUp} ${duration.base} ${easing.entrance} both;

  ${up('md')} {
    padding: ${spacing.md};
    gap: ${spacing.md};
    flex-wrap: nowrap;
  }
`;

export const DetailIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  flex: 1 1 auto;
`;

export const DetailStats = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

/* ─────────────────────────  Sections  ───────────────────────── */

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  margin-top: ${spacing.lg};
  animation: ${fadeInUp} ${duration.base} ${easing.entrance} both;
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding-bottom: ${spacing.xs};
  border-bottom: 1px solid ${palette.line};
`;

export const SectionTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const SectionEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.ui};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
`;

/* ────────────────  Explainer: intent vs computed result  ──────────────── */

export const ExplainerCard = styled.div`
  box-sizing: border-box;
  display: grid;
  gap: ${spacing.sm};
  grid-template-columns: 1fr;
  padding: ${spacing.sm};
  margin-top: ${spacing.md};
  background: ${palette.surfaceMuted};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};

  ${up('md')} {
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    padding: ${spacing.md};
    gap: ${spacing.md};
  }
`;

export const ExplainerHalf = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const ExplainerArrow = styled.div`
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: ${radius.circle};
  background: ${palette.irisTint};
  color: ${palette.irisDark};
  font-size: 16px;
  font-weight: ${typography.weight.bold};
  justify-self: start;

  ${up('md')} {
    justify-self: center;
  }
`;

/* Callout for the "and every building added later" promise. */
export const OrgWideBanner = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.irisTintSoft};
  border: 1px solid ${palette.irisTint};
  animation: ${fadeIn} ${duration.base} ${easing.standard} both;
`;

export const BannerIcon = styled.div`
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: ${radius.circle};
  background: ${palette.irisTint};
  color: ${palette.irisDark};
  display: grid;
  place-items: center;
  font-size: 16px;
`;

export const WarningBanner = styled(OrgWideBanner)`
  background: ${palette.warningSoft};
  border-color: ${status.warning.dot};
`;

export const WarningIcon = styled(BannerIcon)`
  background: ${palette.warningSoft};
  color: ${status.warning.fg};
`;

/* ─────────────────────────  Grant cards  ───────────────────────── */

export const GrantList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

/* A motion row: the caller spreads listItemMotion(index) so cards arrive in
   a cascade, slide when a sibling leaves, and animate out inside a Presence.
   The hover lift is framer's (`whileHover`), not CSS — framer owns the
   element's transform once it has animated y, and a CSS translate would be
   overridden by the inline `transform: none` it leaves at rest. Colours are
   the only thing the CSS transition still moves. */
export const GrantCard = m.div<{ isClosed?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-left: 3px solid
    ${({ isClosed }) => (isClosed ? palette.lineStrong : palette.iris)};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
  opacity: ${({ isClosed }) => (isClosed ? 0.7 : 1)};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lineStrong};
    border-left-color: ${({ isClosed }) =>
      isClosed ? palette.lineStrong : palette.iris};
    box-shadow: ${shadow.sm};
  }

  ${up('md')} {
    padding: ${spacing.md};
  }
`;

export const GrantTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
`;

export const GrantTarget = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const GrantMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs} ${spacing.sm};
`;

export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
`;

/* ─────────────────────────  Resolved access  ───────────────────────── */

export const ResolvedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

/* Same contract as GrantCard: a motion row, hover lift via whileHover. */
export const ResolvedRow = m.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lineStrong};
    box-shadow: ${shadow.sm};
  }

  ${up('md')} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing.sm};
  }
`;

export const ResolvedIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const ResolvedVia = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
`;

export const ViaLabel = styled.span`
  font-family: ${typography.ui};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slateLight};
`;

/* ─────────────────────────  Add-grant form  ───────────────────────── */

export const FormCard = styled.form`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};

  ${up('md')} {
    padding: ${spacing.md};
    gap: ${spacing.md};
  }
`;

/* Wraps the grant form's fields so ONE `disabled` makes every control inside
   inert while the create is in flight. Reset to nothing: a fieldset defaults
   to a border, padding and a content min-width. */
export const FormFieldset = styled.fieldset`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;

  ${up('md')} {
    gap: ${spacing.md};
  }
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.sm};

  ${up('md')} {
    grid-template-columns: 1fr 1fr;
    gap: ${spacing.md};
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const Label = styled.label`
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.inkSoft};
`;

export const HelpText = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: 12px;
  font-weight: ${typography.weight.regular};
  line-height: ${typography.leading.normal};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.slate};
`;

export const StyledSelect = styled.select`
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  width: 100%;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 16px;
  letter-spacing: ${typography.tracking.snug};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    border-color: ${palette.lineStrong};
  }
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
  &:disabled {
    cursor: not-allowed;
    background: ${palette.surfaceMuted};
    color: ${palette.slateLight};
  }
`;

/* Capability toggle — the label carries the plain-language explanation, so the
   row is deliberately tall and the help text is part of the click target. */
export const ToggleRow = styled.label<{ isDisabled?: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ isDisabled }) => (isDisabled ? 0.6 : 1)};
  transition: ${transition.colors};

  &:hover {
    border-color: ${({ isDisabled }) =>
      isDisabled ? palette.line : palette.lineStrong};
    background: ${({ isDisabled }) =>
      isDisabled ? palette.surface : palette.irisTintSoft};
  }

  input[type='checkbox'] {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    margin-top: ${spacing.half};
    accent-color: ${palette.lagoon};
    cursor: inherit;
  }
  /* The input inside is what takes focus; without this the row it lives in gave
     a mouse a hover state and a keyboard nothing at all. */
  &:focus-within {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

export const ToggleCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const ToggleTitle = styled.span`
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

export const FormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: ${spacing.sm};
`;

export const InlineError = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.alertSoft};
  border: 1px solid ${palette.alert};
  color: ${status.alert.fg};
  font-family: ${typography.ui};
  font-size: 14px;
  line-height: ${typography.leading.normal};
  letter-spacing: ${typography.tracking.snug};
`;

export const InlineSuccess = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.successSoft};
  border: 1px solid ${status.success.dot};
  color: ${status.success.fg};
  font-family: ${typography.ui};
  font-size: 14px;
  line-height: ${typography.leading.normal};
  letter-spacing: ${typography.tracking.snug};
`;

/* ─────────────────────────  States  ───────────────────────── */

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  text-align: center;
  padding: ${spacing.xxl} ${spacing.sm};
`;

export const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${radius.circle};
  background: ${palette.irisTint};
  color: ${palette.lagoon};
  display: grid;
  place-items: center;
  font-size: ${typography.size.xxl}px;
`;

export const SectionEmpty = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.half};
  text-align: center;
  padding: ${spacing.lg} ${spacing.sm};
  background: ${palette.surfaceMuted};
  border: 1px dashed ${palette.lineStrong};
  border-radius: ${radius.md};
`;
